// ----- Phase 5.25.3 — FDA tab content ----- //
//
// State machine:
//   idle       → text input + "Look up" button
//   looking-up → spinner with the application number being queried
//   parsed     → ImportPreviewPanel rendered with the openFDA-sourced data
//   error      → error block + "Try again" button → idle
// Public FDA history remains a reference lookup; it cannot establish eCTD sequence identity.
//
// Mirrors XmlTab.tsx structurally so the Import page's three tabs feel
// uniform. Only the entry surface differs (single text input vs.
// file picker / dropzone).
//
// FDA-specific UX notes:
//   - The operator may type just digits ("225431"), a prefixed form
//     ("NDA-225431"), or whatever's pasted from an internal tracker.
//     The backend normalizes; we don't constrain the input client-side
//     beyond "must contain at least one digit." A purely-letters input
//     is short-circuited locally to avoid a guaranteed 400 round-trip.
//   - The 404 NotFound path renders the same error block as upstream
//     failures but with a friendlier "No FDA record" message — operators
//     should not see raw API error codes.

import { useState, type FormEvent, type ReactElement } from 'react';
import { ApiError } from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { importApi } from '@/lib/api/import';
import {
  ImportPreviewPanel,
  type ImportParsedData,
} from '@/components/import/ImportPreviewPanel';

type TabState =
  | { kind: 'idle' }
  | { kind: 'looking-up'; applicationNumber: string }
  | { kind: 'parsed'; data: ImportParsedData; applicationNumber: string }
  | { kind: 'error'; message: string; detail?: string };

const LOOKUP_ERROR_MESSAGES: Record<string, string> = {
  InvalidApplicationNumber: 'Enter a valid FDA application number.',
  NotFound: 'No FDA record found for that application number.',
  RateLimited: 'openFDA rate limit reached — please try again in a minute.',
  UpstreamError: 'openFDA returned an error. Please try again.',
  Timeout: 'openFDA took too long to respond — please try again.',
  NetworkError: 'Could not reach openFDA. Check your connection and retry.',
  AuthRequired: 'Your session expired. Please sign in again.',
  PermissionDenied: 'You do not have permission to import.',
};

export function FdaTab(): ReactElement {
  const [state, setState] = useState<TabState>({ kind: 'idle' });
  const [input, setInput] = useState('');

  const performLookup = async (raw: string): Promise<void> => {
    const trimmed = raw.trim();
    if (trimmed === '') return;
    // Local short-circuit: any digit in the string is enough for the
    // backend's normalizer. Saves a round-trip for obviously bad input.
    if (!/\d/.test(trimmed)) {
      setState({
        kind: 'error',
        message: LOOKUP_ERROR_MESSAGES['InvalidApplicationNumber']!,
      });
      return;
    }
    setState({ kind: 'looking-up', applicationNumber: trimmed });
    try {
      const data = await importApi.lookupFda(trimmed);
      setState({ kind: 'parsed', data, applicationNumber: trimmed });
    } catch (err) {
      const message = formatApiError(err, 'FDA lookup failed.', LOOKUP_ERROR_MESSAGES);
      let detail: string | undefined;
      if (err instanceof ApiError) {
        try {
          const body = JSON.parse(err.body) as { message?: string };
          if (body.message) detail = body.message;
        } catch {
          /* ignore */
        }
      }
      setState(
        detail !== undefined ? { kind: 'error', message, detail } : { kind: 'error', message },
      );
    }
  };

  const onSubmit = (e: FormEvent<globalThis.HTMLFormElement>): void => {
    e.preventDefault();
    void performLookup(input);
  };

  const handlePreviewChange = (next: ImportParsedData): void => {
    setState((prev) => (prev.kind === 'parsed' ? { ...prev, data: next } : prev));
  };

  const reset = (): void => {
    setState({ kind: 'idle' });
  };

  return (
    <div data-testid="fda-tab">
      {state.kind === 'idle' && (
        <form
          data-testid="fda-lookup-form"
          onSubmit={onSubmit}
          style={{
            background: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            padding: 20,
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Look up FDA application
            </div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>
              Enter an NDA, ANDA, or BLA number. Examples: <code>NDA-225431</code>,{' '}
              <code>225431</code>, <code>ANDA 207780</code>.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label htmlFor="fda-application-number" style={{ display: 'none' }}>
              FDA application number
            </label>
            <input
              id="fda-application-number"
              data-testid="fda-application-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="NDA-225431"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: 14,
                border: '1px solid #D1D5DB',
                borderRadius: 4,
                fontFamily: 'monospace',
              }}
            />
            <button
              type="submit"
              data-testid="fda-lookup-btn"
              disabled={input.trim() === ''}
              style={{
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                background: input.trim() === '' ? '#E5E7EB' : '#2563EB',
                color: input.trim() === '' ? '#9CA3AF' : '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: input.trim() === '' ? 'not-allowed' : 'pointer',
              }}
            >
              Look up
            </button>
          </div>
        </form>
      )}

      {state.kind === 'looking-up' && (
        <div
          data-testid="fda-looking-up"
          style={{
            padding: '24px 16px',
            textAlign: 'center',
            fontSize: 13,
            color: '#6B7280',
          }}
        >
          <div style={{ fontSize: 32, marginBottom: 12 }} aria-hidden="true">
            ⏳
          </div>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Looking up {state.applicationNumber}…
          </div>
          <div style={{ fontSize: 12 }}>Querying openFDA</div>
        </div>
      )}

      {state.kind === 'parsed' && (
        <>
          <ImportPreviewPanel
            parsedData={state.data}
            onChange={handlePreviewChange}
            onCancel={reset}
          />
        </>
      )}

      {state.kind === 'error' && (
        <div
          data-testid="fda-lookup-error"
          role="alert"
          style={{
            padding: 16,
            background: '#FEE2E2',
            color: '#B91C1C',
            border: '1px solid #B91C1C',
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{state.message}</div>
          {state.detail && (
            <div data-testid="fda-lookup-error-detail" style={{ fontSize: 12, opacity: 0.85 }}>
              {state.detail}
            </div>
          )}
          <button
            type="button"
            data-testid="fda-try-again-btn"
            onClick={reset}
            style={{
              marginTop: 12,
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              background: '#fff',
              color: '#B91C1C',
              border: '1px solid #B91C1C',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      )}

    </div>
  );
}
